import { Combobox } from "@/components/ui/combobox";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/lib/api";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** Which project a meeting was about, changeable after the fact. `path` is the
 *  endpoint that stores it (a recording, or an analysis of a pasted transcript). */
export function MeetingProjectPicker({
  path,
  projectId,
  projects,
  onSaved,
}: {
  path: string;
  projectId: string | null;
  projects: Array<{ id: string; name: string }>;
  onSaved?: (projectId: string | null) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(projectId ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setValue(projectId ?? ""), [projectId]);

  async function save(next: string) {
    const previous = value;
    setValue(next);
    setSaving(true);
    try {
      await api.patch(path, { projectId: next || null });
      onSaved?.(next || null);
    } catch {
      setValue(previous);
      toast({ title: t("analysisTools.meetingProject.saveFailed"), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Combobox
      size="sm"
      className="w-48"
      aria-label={t("analysisTools.meetingProject.label")}
      data-testid="meeting-project"
      value={value}
      onChange={save}
      disabled={saving}
      options={[
        { value: "", label: t("analysisTools.meetingProject.none") },
        ...projects.map((p) => ({ value: p.id, label: p.name })),
      ]}
    />
  );
}
